import { Fragment, createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";
import { appIcon } from "./icons";
import type {
    ClientActionElement,
    ClientActionRenderPlan,
    FormButtonElement,
} from "../core";

// Map of `<container ref="X"/>` DSL ref-names → live DOM elements.
// Populated by the layout renderer as each `<container>` mounts, drained
// as each unmounts. Plugin-style client actions read this via
// `useClientActionLayoutRefs()` to grab DOM handles for imperative-
// library setup (three.js scene mount, Leaflet map init, AG-Grid
// attach, ...). Held in React state — not just a mutable ref —
// so consuming components re-render when a handle becomes
// available, and so unmount → re-mount cycles propagate cleanly.
export type ClientActionLayoutRefs = Record<string, HTMLDivElement>;

const _LayoutRefsCtx = createContext<ClientActionLayoutRefs | null>(null);

/** Subscribe to the layout's container ref map.

    Returns an empty object when called outside a
    `<ClientActionLayoutRenderer>` subtree, so consumers can be
    unconditionally hooked without a runtime guard — they'll just
    see empty refs and their `useEffect` deps won't fire.

    Pattern for a plugin-style component:

        function MyScene() {
            const refs = useClientActionLayoutRefs();
            useEffect(() => {
                if (!refs.viewer) return;
                const dispose = mountThreeJsScene(refs.viewer);
                return () => dispose();
            }, [refs.viewer]);
            return null;   // imperative; no React DOM
        }

    The map identity changes when refs come and go, so React's
    referential-equality dep check fires the effect only when a
    specific ref's element changes. */
export function useClientActionLayoutRefs(): ClientActionLayoutRefs {
    return useContext(_LayoutRefsCtx) ?? {};
}

// ClientActionLayoutRenderer — walks a parsed `<ClientActionView>` layout
// (Enhancement 08) and renders the three slots: header (top toolbar) +
// body (content with imperative ref slots / component mount slot) +
// footer (status / hint band).
//
// Five element types per slot:
//   - `button`     → reuses FormButtonElement; clicks route through `onCommand(button)`
//   - `container`  → bare <div ref={…}> emitted via `onRegisterRef(name, el)`; the
//                    registered client component grabs it for imperative mounts
//                    (three.js, Leaflet, AG-Grid, …)
//   - `component`  → mounts the `children` prop here (the descriptor's React
//                    component, if any). Useful for pure-React client actions.
//   - `hint`       → small muted text
//   - `ribbon`     → coloured pill with optional tone
//
// The renderer is presentation-only: button click → call `onCommand`,
// container DOM-mount → call `onRegisterRef`. State and side effects
// live in the parent (ClientActionPage).

export interface ClientActionLayoutRendererProps {
    plan: ClientActionRenderPlan;
    /** Mounted in the `<component/>` slot, if the layout has one. */
    children?: ReactNode;
    /** Fires for every header / body / footer button click. The element
        carries `name` (DSL `<button name="...">`) which the plugin's
        onCommand handler switches on. */
    onCommand?: (button: FormButtonElement) => void;
    /** Fires whenever a `<container ref="X"/>` DOM element mounts (and
        again with `null` on unmount). Plugins use this to grab DOM
        handles for imperative library setup. */
    onRegisterRef?: (refName: string, el: HTMLDivElement | null) => void;
}

export function ClientActionLayoutRenderer({
    plan,
    children,
    onCommand,
    onRegisterRef,
}: ClientActionLayoutRendererProps): ReactNode {
    // State-backed map so plugin children (consumed via the
    // `useClientActionLayoutRefs()` hook) re-render the moment a
    // <container> mounts. The outer `onRegisterRef` callback prop
    // is still honoured so non-hook consumers (raw imperative code
    // owning the renderer instance) can also receive each ref event.
    const [refs, setRefs] = useState<ClientActionLayoutRefs>({});
    const setContainerRef = useCallback((name: string, el: HTMLDivElement | null) => {
        onRegisterRef?.(name, el);
        setRefs((prev) => {
            if (el) {
                if (prev[name] === el) return prev;
                return { ...prev, [name]: el };
            }
            if (!(name in prev)) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    }, [onRegisterRef]);

    const renderItem = (item: ClientActionElement, key: string): ReactNode => {
        switch (item.type) {
            case "button":
                return (
                    <LayoutButton key={key} button={item} onCommand={onCommand} />
                );
            case "container":
                return (
                    <_LayoutContainer
                        key={key}
                        name={item.ref}
                        className={item.class}
                        setRef={setContainerRef}
                    />
                );
            case "component":
                // Render the descriptor's React component (passed in as
                // children) into this slot. Multiple <component/> elements
                // in a single layout repeat the same children React node —
                // unusual but legal; consumers should declare one.
                return <Fragment key={key}>{children}</Fragment>;
            case "hint":
                return (
                    <span key={key} className="client-action__hint">
                        {item.text}
                    </span>
                );
            case "ribbon":
                return (
                    <span
                        key={key}
                        className={`client-action__ribbon client-action__ribbon--${item.tone ?? "neutral"}`}
                    >
                        {item.label}
                    </span>
                );
            default:
                return null;
        }
    };

    const renderSlot = (name: "header" | "body" | "footer", items: ClientActionElement[]): ReactNode => {
        if (items.length === 0) return null;
        return (
            <div className={`client-action__${name}`} data-testid={`client-action-${name}`}>
                {items.map((item, i) => renderItem(item, `${name}-${i}`))}
            </div>
        );
    };

    return (
        <_LayoutRefsCtx.Provider value={refs}>
            <div
                className={`client-action-layout ${plan.size ? `client-action-layout--${plan.size}` : ""}`.trim()}
                data-view-id={plan.modelKey}
            >
                {plan.title ? <h2 className="client-action__title">{plan.title}</h2> : null}
                {renderSlot("header", plan.header)}
                {renderSlot("body", plan.body)}
                {renderSlot("footer", plan.footer)}
            </div>
        </_LayoutRefsCtx.Provider>
    );
}

// Per-container subcomponent so React sees a STABLE ref callback
// across re-renders. The previous inline form
//     ref={(el) => setContainerRef(item.ref, el)}
// gave the div a fresh callback identity each render — React detached
// the old one (null) and re-attached the new one (el) every cycle, and
// because each detach/attach calls setRefs, we hit an infinite loop.
// By extracting the wrapper and useCallback-binding the ref callback to
// (name, setRef), React only invokes the ref on real mount / unmount.
interface _LayoutContainerProps {
    name: string;
    className?: string;
    setRef: (name: string, el: HTMLDivElement | null) => void;
}
function _LayoutContainer({ name, className, setRef }: _LayoutContainerProps) {
    const bind = useCallback((el: HTMLDivElement | null) => setRef(name, el), [name, setRef]);
    return (
        <div
            ref={bind}
            className={`client-action__container ${className ?? ""}`.trim()}
            data-ref={name}
        />
    );
}

interface LayoutButtonProps {
    button: FormButtonElement;
    onCommand?: (button: FormButtonElement) => void;
}

function LayoutButton({ button, onCommand }: LayoutButtonProps) {
    const Icon = button.icon ? appIcon(button.icon) : null;
    const label = button.label ?? button.name;
    return (
        <button
            type="button"
            className="client-action__btn"
            onClick={() => onCommand?.(button)}
            data-button-name={button.name}
            data-testid={`client-action-button-${button.name}`}
        >
            {Icon ? <Icon size={14} aria-hidden="true" /> : null}
            <span>{label}</span>
        </button>
    );
}
